import { SearchModel } from '@internal/blocks/SearchResultsBlock/TSearchParams';
import { useSearchResultsRouting } from '@internal/blocks/SearchResultsBlock/useSearchResultsRouting';
import { translations } from '@internal/i18n/translations';
import { suggest } from '@internal/services/apiClient';
import { localeNamespace } from '@internal/utils/machine/i18n/Locale';
import { TLocale } from '@internal/utils/machine/i18n/TLocale';
import { Machine } from '@internal/utils/machine/Machine';
import {
    MachineType,
    MachineTypeSchema,
} from '@internal/utils/machine/MachineType';
import { useMachineTypeName } from '@internal/utils/machine/useMachineTypeName';
import { useModelLabel } from '@internal/utils/machine/useModelLabel';
import { getMediaUrl } from '@internal/utils/media/getMediaSrc';
import { omitUndefined } from '@internal/utils/omitUndefined';
import { useMachineUrl } from '@internal/utils/routing/hooks/useMachineUrl';
import { useUserCountryCodeForSearch } from '@internal/utils/user/useUserCountryCodeForSearch';
import { useRefState } from '@internal/utils/useRefState';
import {
    AutocompleteCloseReason,
    AutocompleteRenderInputParams,
    ClickAwayListener,
} from '@mui/material';
import {
    LoadValuesHandler,
    RemoteAutoComplete,
} from '@renderer-ui-library/molecules/RemoteAutocomplete/RemoteAutoComplete';
import { SelectFilterOption } from '@renderer-ui-library/molecules/RemoteAutocomplete/SelectFilterOption';
import { useMediaQuery } from '@renderer-ui-library/mui/utils/mediaQuery/useMediaQuery';
import { uniqBy } from 'lodash';
import { useTranslation } from 'next-i18next';
import { useRouter } from 'next/router';
import {
    memo,
    MouseEvent,
    SyntheticEvent,
    useCallback,
    useEffect,
    useState,
} from 'react';
import { z } from 'zod';
import { SearchInput } from '../../SearchInput/SearchInput';
import styles from './searchAutosuggest.module.scss';

type FiltersMap = {
    brand?: string | null;
    machineType?: MachineType | null;
    model?: {
        mapped?: string;
        raw?: string;
        base?: string;
        series?: string;
    } | null;
};

const RecentSearchesFiltersMapSchema = z.object({
    brand: z.string().optional(),
    machineType: MachineTypeSchema.optional(),
    model: z
        .object({
            mapped: z.string().optional(),
            raw: z.string().optional(),
            base: z.string().optional(),
            series: z.string().optional(),
        })
        .optional(),
});

export type RecentSearchesFiltersMap = z.infer<
    typeof RecentSearchesFiltersMapSchema
>;
const RecentSearchesSchemaV1 = z.object({
    version: z.literal(1),
    filters: z.array(RecentSearchesFiltersMapSchema),
});

export type RecentSearches = z.infer<typeof RecentSearchesSchemaV1>;

const loadRecentSearches = () => {
    const recentSearches = localStorage.getItem('recentSearches');
    if (recentSearches) {
        let data: unknown;
        try {
            data = JSON.parse(recentSearches);
        } catch (error) {
            return null;
        }
        const parsedData = RecentSearchesSchemaV1.safeParse(data);
        if (parsedData.success) {
            return parsedData.data;
        }
    }
    return null;
};

type AutocompleteValue = SelectFilterOption & {
    listItemLabel: string;
    label: string;
} & (
        | { type: 'machine'; machine: Machine }
        | { type: 'search'; values: FiltersMap }
    );

const generateCountLabel = (count: number | undefined | null) => {
    if (count === undefined || count === null) return '';
    if (count === 0) return '(0)';
    const roundedCount = Math.max(0, Math.floor(count / 10) * 10);
    return count > 9 ? `(${roundedCount}+)` : `(~${count})`;
};

const filtesMapModelToSearchModel = (
    model: FiltersMap['model'],
): SearchModel | null => {
    if (model?.base) {
        return { name: model.base, type: 'base' };
    }
    if (model?.series) {
        return { name: model.series, type: 'series' };
    }
    if (model?.raw) {
        return { name: model.raw, type: 'fuzzy' };
    }
    if (model?.mapped) {
        return { name: model.mapped, type: 'mapped' };
    }
    return null;
};

export const SearchAutosuggest: React.FC<{
    locale: TLocale;
    isOnSerp: boolean;
}> = memo(({ locale, isOnSerp }) => {
    const { isMobile } = useMediaQuery();
    const { t } = useTranslation(localeNamespace.common);
    const { getMachineTypeName } = useMachineTypeName();
    const { getUserCountryCode } = useUserCountryCodeForSearch();
    const { getModelLabel: getModelLabelFromHook } = useModelLabel();
    const [values, setValues, valuesRef] = useRefState<AutocompleteValue[]>([]);
    const [isOpen, setIsOpen] = useState(false);
    const [inputValue, setInputValue] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const { gotoSearchResults } = useSearchResultsRouting();
    const [recentSearches, setRecentSearches, recentSearchesRef] =
        useRefState<RecentSearches>({ version: 1, filters: [] });
    const { getMachineUrl } = useMachineUrl();
    const { push: gotoUrl } = useRouter();

    useEffect(() => {
        const recentSearches = loadRecentSearches();
        if (recentSearches) {
            setRecentSearches(recentSearches);
        }
    }, [setRecentSearches]);
    const handleOpenRequest = useCallback(() => setIsOpen(true), []);
    const handleCloseRequest = useCallback(
        (event: SyntheticEvent, reason: AutocompleteCloseReason) => {
            if (reason === 'blur') {
                return;
            }
            setIsOpen(false);
        },
        [],
    );

    const getModelValue = useCallback((model: FiltersMap['model']) => {
        return (
            model?.base || model?.series || model?.raw || model?.mapped || ''
        );
    }, []);

    const appendToRecentSearches = useCallback(
        (filters: FiltersMap) => {
            setRecentSearches(prev => {
                const filtersForStorage: RecentSearchesFiltersMap =
                    omitUndefined({
                        brand: filters.brand ?? undefined,
                        machineType: filters.machineType ?? undefined,
                        model: filters.model
                            ? omitUndefined({
                                  mapped: filters.model.mapped ?? undefined,
                                  raw: filters.model.raw ?? undefined,
                                  base: filters.model.base ?? undefined,
                                  series: filters.model.series ?? undefined,
                              })
                            : undefined,
                    } satisfies RecentSearchesFiltersMap);

                const newRecentSearches: RecentSearches = {
                    ...prev,
                    filters: uniqBy(
                        [filtersForStorage, ...prev.filters],
                        f =>
                            `${f.brand}|${f.machineType}|${getModelValue(f.model)}`,
                    ).slice(0, 5),
                };

                try {
                    localStorage.setItem(
                        'recentSearches',
                        JSON.stringify(newRecentSearches),
                    );
                } catch (err) {
                    // ignore silently
                }
                return newRecentSearches;
            });
        },
        [getModelValue, setRecentSearches],
    );

    const handleLoadingStateChange = useCallback((isLoading: boolean) => {
        setIsLoading(isLoading);
    }, []);

    const getModelLabel = useCallback(
        (model: FiltersMap['model']) => {
            if (model?.base) {
                return getModelLabelFromHook(model.base, 'base');
            }
            if (model?.series) {
                return getModelLabelFromHook(model.series, 'series');
            }
            if (model?.raw) {
                return getModelLabelFromHook(model.raw, 'fuzzy');
            }
            if (model?.mapped) {
                return getModelLabelFromHook(model.mapped, 'mapped');
            }
            return '';
        },
        [getModelLabelFromHook],
    );

    const generateModelItemLabel = useCallback(
        (options: {
            brand?: string;
            model: FiltersMap['model'];
            machineType?: MachineType;
            count?: number;
        }) => {
            const brandLabel = options.brand ? `${options.brand} ` : '';
            const machineTypeLabel = options.machineType
                ? `${getMachineTypeName(options.machineType, 'plural')} `
                : '';
            const prefix = `${brandLabel}${machineTypeLabel}`;
            return `${prefix}${options.model?.raw && prefix ? ' + ' : ''}${getModelLabel(options.model)} ${options.count !== undefined ? generateCountLabel(options.count) : ''}`.trim();
        },
        [getMachineTypeName, getModelLabel],
    );

    const generateFuzzyLabel = useCallback(
        (options: {
            brand?: string;
            model: string;
            machineType?: MachineType;
            count: number;
        }) => {
            const brandLabel = options.brand ? `${options.brand} ` : '';
            const machineTypeLabel = options.machineType
                ? `${getMachineTypeName(options.machineType, 'plural')} `
                : '';
            return `${brandLabel}${machineTypeLabel}"${options.model}" ${generateCountLabel(options.count)}`;
        },
        [getMachineTypeName],
    );

    // this gets shown in the input after selecting an item from the options list
    const generateModelLabel = useCallback(
        (options: {
            brand?: string;
            model: string;
            machineType?: MachineType;
        }) => {
            const brandLabel = options.brand ? `${options.brand} ` : '';
            const modelLabel = options.model ? `${options.model} ` : '';
            const machineTypeLabel =
                options.machineType && !(options.brand && options.model)
                    ? `${getMachineTypeName(options.machineType, 'plural')} `
                    : '';
            return `${brandLabel}${machineTypeLabel}${modelLabel}`;
        },
        [getMachineTypeName],
    );

    // it also merges values with same keys
    const autocompleteValuesToFiltersMap = useCallback(
        (values: AutocompleteValue[]): FiltersMap => {
            const filtersMap: FiltersMap = {};
            for (const newValue of values) {
                if (newValue.type !== 'search') {
                    continue;
                }

                Object.assign(filtersMap, newValue.values);
            }

            return filtersMap;
        },
        [],
    );

    const filtersMapToAutocompleteValues = useCallback(
        (filtersMap: FiltersMap): AutocompleteValue[] => {
            const values: AutocompleteValue[] = [];

            const { brand, machineType, model } = filtersMap;

            if (brand) {
                values.push({
                    type: 'search',
                    value: `brandMatch|brand:${brand}`,
                    listItemLabel: brand,
                    label: brand,
                    values: {
                        brand,
                    },
                });
            }

            if (machineType) {
                const machineTypeName = getMachineTypeName(
                    machineType,
                    'plural',
                );
                values.push({
                    type: 'search',
                    value: `machineTypeMatch|machineType:${machineType}`,
                    listItemLabel: machineTypeName,
                    label: machineTypeName,
                    values: {
                        machineType,
                    },
                });
            }

            if (model) {
                const label = getModelLabel(model);
                values.push({
                    type: 'search',
                    value: `modelMatch|model:${label}`,
                    listItemLabel: label,
                    label: getModelValue(model),
                    values: {
                        model,
                    },
                });
            }

            return values;
        },
        [getMachineTypeName, getModelLabel, getModelValue],
    );

    const applySearch = useCallback(
        (filters: FiltersMap) => {
            const model = filtesMapModelToSearchModel(filters.model);
            gotoSearchResults(
                {
                    brands: filters.brand ? [filters.brand] : [],
                    machineType: filters.machineType ?? undefined,
                    models: model ? [model] : undefined,
                },
                isOnSerp,
            );
        },
        [gotoSearchResults, isOnSerp],
    );

    const handleSearchButtonClick = useCallback(() => {
        applySearch(autocompleteValuesToFiltersMap(valuesRef.current));
        setIsOpen(false);
    }, [applySearch, autocompleteValuesToFiltersMap, valuesRef]);

    const handleChange = useCallback(
        (newValues: AutocompleteValue[]) => {
            // converts the filters into a map while merging filters with same attributes
            const filters = autocompleteValuesToFiltersMap(newValues);
            if (Object.keys(filters).length > 0) {
                appendToRecentSearches(filters);
            }
            const processedValues = filtersMapToAutocompleteValues(filters);
            setValues(processedValues);

            const machine = newValues.find(value => value.type === 'machine');
            if (machine?.type === 'machine') {
                gotoUrl(getMachineUrl(locale, machine.machine));
                setIsOpen(false);
                return;
            }

            if (filters.model) {
                setIsOpen(false);
            }

            if (filters.model || isOnSerp) {
                applySearch(filters);
            }
        },
        [
            autocompleteValuesToFiltersMap,
            filtersMapToAutocompleteValues,
            setValues,
            isOnSerp,
            appendToRecentSearches,
            gotoUrl,
            getMachineUrl,
            locale,
            applySearch,
        ],
    );

    const handleInputChange = useCallback(
        (ev: SyntheticEvent, value: string) => {
            setInputValue(value);
        },
        [],
    );

    const topMatchesTitle = t(translations.searchSuggestGroupTopMatchesTitle);
    const doYouMeanTitle = t(
        translations.searchSuggestGroupTopMatchesDoYouMeanTitle,
    );

    const loadValue = useCallback<LoadValuesHandler<AutocompleteValue>>(
        async ({ input, abortSignal }): Promise<AutocompleteValue[]> => {
            let preSelectedBrand: string | undefined;
            const brandValue = valuesRef.current.find(
                v => v.type === 'search' && v.values.brand,
            );
            if (brandValue?.type === 'search' && brandValue.values.brand) {
                preSelectedBrand = brandValue.values.brand;
            }

            let preSelectedMachineType: MachineType | undefined;
            const machienTypeValue = valuesRef.current.find(
                v => v.type === 'search' && v.values.machineType,
            );
            if (
                machienTypeValue?.type === 'search' &&
                machienTypeValue.values.machineType
            ) {
                preSelectedMachineType = machienTypeValue.values.machineType;
            }

            return suggest({
                query: input,
                BRAND: preSelectedBrand,
                MACHINE_TYPE: preSelectedMachineType,
                locale,
                abortSignal,
                countryCode: await getUserCountryCode(),
            }).then(
                ({
                    idMatch,
                    brandMatch,
                    brandSuggestions,
                    machineTypeMatch,
                    machineTypeSuggestions,
                    brandAndMachineTypeMatch,
                    modelMatch,
                    modelSuggestions,
                    modelSearch,
                    // topMachineTypesByBrand,
                    // topBrandsByMachineType,
                }) => {
                    const entries: AutocompleteValue[] = [];

                    // ID MATCHES ---------------------------------------------
                    if (idMatch) {
                        const machine = idMatch;
                        const imageUrl = machine.previewImage
                            ? getMediaUrl(machine.previewImage, {
                                  width: 36,
                                  height: 36,
                                  disableMachineWatermark: true,
                              })
                            : undefined;

                        entries.push({
                            type: 'machine',
                            machine,
                            value: `machineMatch|machineGroupId:${machine.machineGroupId}`,
                            key: machine.machineGroupId + input,
                            listItemLabel: machine.name,
                            label: machine.machineGroupId,
                            imageUrl,
                            description: (
                                <div>
                                    {[
                                        machine.machineGroupId,
                                        machine.yearOfProduction,
                                        machine.locationCountry,
                                    ]
                                        .filter(
                                            (value): value is string =>
                                                value !== null,
                                        )
                                        .join(', ')}
                                </div>
                            ),
                        });

                        return entries;
                    }
                    // --------------------------------------------------------

                    // MODEL MATCH --------------------------------------------
                    if (modelMatch) {
                        const machineType =
                            modelMatch.MACHINE_TYPE as MachineType;
                        if ('MODEL' in modelMatch) {
                            entries.push({
                                type: 'search',
                                value: `modelMatch|model:${modelMatch.MODEL},brand:${modelMatch.BRAND},machineType:${modelMatch.MACHINE_TYPE}`,
                                listItemLabel: generateModelItemLabel({
                                    model: {
                                        mapped: modelMatch.MODEL,
                                    },
                                    brand: modelMatch.BRAND,
                                    count: modelMatch.count,
                                    machineType,
                                }),
                                label: '',
                                group: topMatchesTitle,
                                values: {
                                    brand: modelMatch.BRAND,
                                    model: {
                                        mapped: modelMatch.MODEL,
                                    },
                                    machineType,
                                },
                            });
                        }

                        if (
                            'BASE_MODEL' in modelMatch &&
                            (!('MODEL' in modelMatch) ||
                                modelMatch.MODEL !== modelMatch.BASE_MODEL)
                        ) {
                            entries.push({
                                type: 'search',
                                value: `modelMatch|baseModel:${modelMatch.BASE_MODEL},brand:${modelMatch.BRAND},machineType:${modelMatch.MACHINE_TYPE}`,
                                listItemLabel: generateModelItemLabel({
                                    model: {
                                        base: modelMatch.BASE_MODEL,
                                    },
                                    brand: modelMatch.BRAND,
                                    count: modelMatch.count,
                                    machineType,
                                }),
                                label: '',
                                group: topMatchesTitle,
                                values: {
                                    brand: modelMatch.BRAND,
                                    model: {
                                        base: modelMatch.BASE_MODEL,
                                    },
                                    machineType,
                                },
                            });
                        }

                        if (modelMatch.MODEL_SERIES) {
                            entries.push({
                                type: 'search',
                                value: `modelMatch|modelSeries:${modelMatch.MODEL_SERIES},brand:${modelMatch.BRAND},machineType:${modelMatch.MACHINE_TYPE}`,
                                listItemLabel: generateModelItemLabel({
                                    model: {
                                        series: modelMatch.MODEL_SERIES,
                                    },
                                    brand: modelMatch.BRAND,
                                    count: modelMatch.count,
                                    machineType,
                                }),
                                label: '',
                                group: topMatchesTitle,
                                values: {
                                    brand: modelMatch.BRAND,
                                    model: {
                                        series: modelMatch.MODEL_SERIES,
                                    },
                                    machineType,
                                },
                            });
                        }
                    }
                    // --------------------------------------------------------

                    // BRAND AND MACHINE TYPE MATCH ---------------------------
                    if (
                        brandAndMachineTypeMatch &&
                        !(preSelectedBrand && preSelectedMachineType)
                    ) {
                        const machineType =
                            brandAndMachineTypeMatch.MACHINE_TYPE as MachineType;
                        const machineTypeName = getMachineTypeName(
                            machineType,
                            'plural',
                        );
                        entries.push({
                            type: 'search',
                            value: `brandAndMachineTypeMatch|brand:${brandAndMachineTypeMatch.BRAND},machineType:${machineType}`,
                            listItemLabel: `${brandAndMachineTypeMatch.BRAND} ${machineTypeName} ${generateCountLabel(brandAndMachineTypeMatch.count)}`,
                            label: `${brandAndMachineTypeMatch.BRAND} ${machineTypeName}`,
                            group: topMatchesTitle,
                            values: {
                                brand: brandAndMachineTypeMatch.BRAND,
                                machineType,
                                model: null,
                            },
                        });
                    }
                    // --------------------------------------------------------

                    // BRAND MATCH --------------------------------------------
                    if (
                        brandMatch &&
                        !preSelectedBrand &&
                        (!brandAndMachineTypeMatch ||
                            brandAndMachineTypeMatch.BRAND !== brandMatch.BRAND)
                    ) {
                        entries.push({
                            type: 'search',
                            value: `brandMatch|brand:${brandMatch.BRAND}`,
                            listItemLabel: `Brand: ${brandMatch.BRAND} ${generateCountLabel(brandMatch.count)}`,
                            label: brandMatch.BRAND,
                            group: topMatchesTitle,
                            values: {
                                brand: brandMatch.BRAND,
                                model: null,
                            },
                        });
                    }
                    // --------------------------------------------------------

                    // MACHINE TYPE MATCH -------------------------------------
                    if (
                        machineTypeMatch &&
                        !preSelectedMachineType &&
                        (!brandAndMachineTypeMatch ||
                            brandAndMachineTypeMatch.MACHINE_TYPE !==
                                machineTypeMatch.MACHINE_TYPE)
                    ) {
                        const machineType =
                            machineTypeMatch.MACHINE_TYPE as MachineType;
                        const machineTypeName = getMachineTypeName(
                            machineType,
                            'plural',
                        );

                        entries.push({
                            type: 'search',
                            value: `machineTypeMatch|machineType:${machineType}`,
                            listItemLabel: `${t(translations.searchSuggestItemLabelMachineType, { name: machineTypeName })} ${generateCountLabel(machineTypeMatch.count)}`,
                            label: machineTypeName,
                            group: topMatchesTitle,
                            values: {
                                machineType,
                                model: null,
                            },
                        });
                    }
                    // --------------------------------------------------------

                    // SUGGESTIONS --------------------------------------------

                    // MODEL SUGGESTIONS
                    // if we have a model match, we want to exclude that here
                    const modelSuggestionsWithouModelMatch = modelMatch
                        ? modelSuggestions.filter(
                              suggestion =>
                                  suggestion.MODEL !==
                                  ('MODEL' in modelMatch
                                      ? modelMatch.MODEL
                                      : ''),
                          )
                        : modelSuggestions;

                    for (const modelSuggestion of modelSuggestionsWithouModelMatch) {
                        const machineType =
                            modelSuggestion.MACHINE_TYPE as MachineType;

                        entries.push({
                            type: 'search',
                            value: `modelSuggestions|model:${modelSuggestion.MODEL},brand:${modelSuggestion.BRAND},machineType:${machineType}`,
                            listItemLabel: generateModelItemLabel({
                                model: {
                                    mapped: modelSuggestion.MODEL,
                                },
                                brand: modelSuggestion.BRAND,
                                count: modelSuggestion.count,
                                machineType,
                            }),
                            label: '',
                            group: doYouMeanTitle,
                            values: {
                                brand: modelSuggestion.BRAND,
                                model: {
                                    mapped: modelSuggestion.MODEL,
                                },
                                machineType,
                            },
                        });
                    }

                    // BRAND SUGGESTIONS
                    const filteredBrandSuggestions = brandSuggestions.filter(
                        suggestion => suggestion.BRAND !== brandMatch?.BRAND,
                    );
                    if (!brandAndMachineTypeMatch && !preSelectedBrand) {
                        entries.push(
                            ...filteredBrandSuggestions.map<AutocompleteValue>(
                                brandSuggestion => ({
                                    type: 'search',
                                    value: `brandSuggestions|brand:${brandSuggestion.BRAND}`,
                                    listItemLabel: `${t(translations.searchSuggestItemLabelBrand, { name: brandSuggestion.BRAND })} ${generateCountLabel(brandSuggestion.count)}`,
                                    label: brandSuggestion.BRAND,
                                    group: doYouMeanTitle,
                                    values: {
                                        brand: brandSuggestion.BRAND,
                                        model: null,
                                    },
                                }),
                            ),
                        );
                    }

                    // MACHINE TYPE SUGGESTIONS
                    if (!brandAndMachineTypeMatch && !preSelectedMachineType) {
                        const filteredMachineTypeSuggestions =
                            machineTypeSuggestions.filter(
                                suggestion =>
                                    suggestion.MACHINE_TYPE !==
                                    machineTypeMatch?.MACHINE_TYPE,
                            );
                        entries.push(
                            ...filteredMachineTypeSuggestions.map<AutocompleteValue>(
                                (machineTypeSuggestion): AutocompleteValue => {
                                    const machineType =
                                        machineTypeSuggestion.MACHINE_TYPE as MachineType;
                                    const machineTypeName = getMachineTypeName(
                                        machineType,
                                        'plural',
                                    );
                                    return {
                                        type: 'search',
                                        value: `machineTypeSuggestion|machineType:${machineType}`,
                                        listItemLabel: `${t(translations.searchSuggestItemLabelMachineType, { name: machineTypeName })} ${generateCountLabel(machineTypeSuggestion.count)}`,
                                        label: machineTypeName,
                                        group: doYouMeanTitle,
                                        values: {
                                            machineType,
                                            model: null,
                                        },
                                    };
                                },
                            ),
                        );
                    }
                    // --------------------------------------------------------

                    // FUZZY SEARCH
                    if (modelSearch) {
                        const machineType =
                            modelSearch.MACHINE_TYPE as MachineType;
                        const filters: FiltersMap = {
                            model: {
                                raw: modelSearch.query,
                            },
                            brand: modelSearch.BRAND ?? null,
                            machineType: machineType ?? null,
                        };

                        entries.push({
                            type: 'search',
                            value: `modelSearch|query:${modelSearch.query}|brand:${modelSearch.BRAND}|machineType:${machineType}`,
                            listItemLabel: generateFuzzyLabel({
                                model: modelSearch.query,
                                brand: modelSearch.BRAND,
                                machineType,
                                count: modelSearch.count,
                            }),
                            label: '',
                            group: t(translations.searchGroupKeywordTitle),
                            values: filters,
                        });
                    }
                    // --------------------------------------------------------

                    // // TOP BRANDS BY MACHINE TYPE -----------------------------
                    // if (
                    //   topMachineTypesByBrand.length > 0 &&
                    //   !preSelectedMachineType &&
                    //   !modelMatch &&
                    //   !machineTypeMatch
                    // ) {
                    //   entries.push(
                    //     ...topMachineTypesByBrand.map<AutocompleteValue>(
                    //       (topMachineType) => {
                    //         const machineType = topMachineType.MACHINE_TYPE as MachineType
                    //         const machineTypeName = getMachineTypeName(
                    //           machineType,
                    //           'plural'
                    //         )
                    //         return {
                    //           type: 'search',
                    //           value: `topMachineTypesByBrand|machineType:${machineType}|brand:${topMachineType.BRAND}`,
                    //           listItemLabel: `${topMachineType.BRAND} ${machineTypeName} ${generateCountLabel(topMachineType.count)}`,
                    //           label: machineTypeName,
                    //           group: t(
                    //             translations.searchSuggestGroupPopularSearchesTitle
                    //           ),
                    //           values: {
                    //             machineType,
                    //             brand: topMachineType.BRAND,
                    //             model: null,
                    //           },
                    //         }
                    //       }
                    //     )
                    //   )
                    // }
                    // // --------------------------------------------------------

                    // // TOP BRANDS BY MACHINE TYPE -----------------------------
                    // // --------------------------------------------------------

                    // if (
                    //   topBrandsByMachineType.length > 0 &&
                    //   !preSelectedBrand &&
                    //   !modelMatch &&
                    //   !brandMatch
                    // ) {
                    //   entries.push(
                    //     ...topBrandsByMachineType.map<AutocompleteValue>((topBrand) => {
                    //       const machineType = topBrand.MACHINE_TYPE as MachineType
                    //       const machineTypeName = getMachineTypeName(
                    //         machineType,
                    //         'plural'
                    //       )
                    //       return {
                    //         type: 'search',
                    //         value: `topBrandsByMachineType|machineType:${machineType}|brand:${topBrand.BRAND}`,
                    //         listItemLabel: `${topBrand.BRAND} ${machineTypeName} ${generateCountLabel(topBrand.count)}`,
                    //         label: topBrand.BRAND,
                    //         group: t(translations.searchSuggestGroupPopularSearchesTitle),
                    //         values: {
                    //           machineType,
                    //           brand: topBrand.BRAND,
                    //           model: null,
                    //         },
                    //       }
                    //     })
                    //   )
                    // }
                    // // --------------------------------------------------------

                    // RECENT SEARCHES
                    entries.push(
                        ...recentSearchesRef.current.filters.map(
                            (filter): AutocompleteValue => ({
                                type: 'search',
                                value: `recentSearches|${JSON.stringify(filter)}`,
                                listItemLabel: generateModelItemLabel({
                                    model: filter.model,
                                    brand: filter.brand,
                                    machineType: filter.machineType,
                                }),
                                label: '',
                                group: t(
                                    translations.searchSuggestGroupRecentSearchesTitle,
                                ),
                                values: {
                                    brand: filter.brand ?? null,
                                    machineType: filter.machineType ?? null,
                                    model: filter.model ?? null,
                                },
                            }),
                        ),
                    );

                    return uniqBy(entries, entry => entry.value);
                },
            );
        },
        [
            doYouMeanTitle,
            generateFuzzyLabel,
            generateModelItemLabel,
            getMachineTypeName,
            getUserCountryCode,
            locale,
            recentSearchesRef,
            t,
            topMatchesTitle,
            valuesRef,
        ],
    );

    // TODO Enable icon button click to trigger search by query
    const renderInput = useCallback(
        (params: AutocompleteRenderInputParams) => (
            <SearchInput
                {...params}
                locale={locale}
                isLoading={isLoading}
                showPlaceholder={values.length === 0 && inputValue === ''}
                onSearchButtonClick={handleSearchButtonClick}
            />
        ),
        [locale, isLoading, values.length, inputValue, handleSearchButtonClick],
    );

    const handleTagClick = useCallback(
        (ev: MouseEvent<HTMLElement>, option: AutocompleteValue) => {
            setValues(prev =>
                prev.filter(value => value.value !== option.value),
            );
            setInputValue(prev => `${option.label} ${prev}`.trim());
        },
        [setValues],
    );

    const handleClickAway = useCallback(() => {
        setIsOpen(false);
    }, []);

    return (
        <ClickAwayListener onClickAway={handleClickAway}>
            <div style={{ width: '100%' }}>
                <RemoteAutoComplete<AutocompleteValue>
                    type="multiple"
                    value={values}
                    onChange={handleChange}
                    loadValues={loadValue}
                    renderInput={renderInput}
                    grouping
                    listboxProps={{
                        style: {
                            maxHeight: isMobile ? '50vh' : '70vh',
                            zIndex: 1000,
                        },
                    }}
                    transparentBackground
                    open={isOpen}
                    onOpen={handleOpenRequest}
                    onClose={handleCloseRequest}
                    autoHighlight
                    onInputChange={handleInputChange}
                    clearOnBlur={false}
                    inputValue={inputValue}
                    onLoadingStateChange={handleLoadingStateChange}
                    onTagClick={handleTagClick}
                    paperClassName={styles.paperStyles}
                />
            </div>
        </ClickAwayListener>
    );
});

SearchAutosuggest.displayName = 'SearchAutosuggest';
